import type { ColumnDef } from "@tanstack/react-table";
import type { Article } from "@/types";
import { ArticleStatus } from "@/types/enums";
import { Badge } from "@trillion/ui/components/ui/badge";
import { createActionsColumn } from "@trillion/ui";

interface ArticleColumnsOptions {
  onEdit: (article: Article) => void;
  onDelete: (article: Article) => void;
}

const articleStatusVariants: Record<ArticleStatus, "default" | "secondary" | "destructive" | "outline"> = {
  [ArticleStatus.Published]: "default",
  [ArticleStatus.Draft]: "secondary",
  [ArticleStatus.Archived]: "outline",
};

// Load-bearing: article_status_id is a numeric enum, so rendering it raw would print the digit.
const articleStatusLabels: Record<ArticleStatus, string> = {
  [ArticleStatus.Published]: "Published",
  [ArticleStatus.Draft]: "Draft",
  [ArticleStatus.Archived]: "Archived",
};

export function getArticleColumns({ onEdit, onDelete }: ArticleColumnsOptions): ColumnDef<Article>[] {
  return [
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => (
        <div className="max-w-[250px] truncate font-medium">{row.getValue("title")}</div>
      ),
    },
    {
      accessorKey: "category",
      header: "Category",
      cell: ({ row }) => {
        const category = row.getValue("category") as string | null;
        return category ? (
          <Badge variant="secondary">{category}</Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      accessorKey: "article_status_id",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original.article_status_id;
        return (
          <Badge variant={articleStatusVariants[status] ?? "secondary"}>
            {articleStatusLabels[status] ?? "Unknown"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "author",
      header: "Author",
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.getValue("author") || "-"}</span>
      ),
    },
    {
      accessorKey: "created_date",
      header: "Created",
      cell: ({ row }) => {
        const date = row.getValue("created_date") as string;
        return (
          <span className="text-muted-foreground">{new Date(date).toLocaleDateString()}</span>
        );
      },
    },
    createActionsColumn<Article>({ onEdit, onDelete }),
  ];
}
